import { useEffect } from "react"
import { Navigate, useLocation, useSearchParams } from "react-router-dom"
import { WinThemesList } from "@/components/strategy/WinThemesList"
import { BidBrief } from "@/components/strategy/BidBrief"
import { BidMemoryList } from "@/components/strategy/BidMemoryList"
import { BidSubNav } from "@/components/bid-menu/BidSubNav"
import { useStrategyStore } from "@/stores/strategy-store"
import { PLAN_VIEWS, SUB_VIEW_PARAM, legacyBidRedirectTarget, parseSubView } from "@/lib/bid-sections"

interface PlanSectionProps {
  bidId: string
  readOnly: boolean
}

/**
 * Plan: the bid brief, memory and win themes. Document setup lives in Write.
 */
export function PlanSection({ bidId, readOnly }: PlanSectionProps) {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const wasDocuments = searchParams.get(SUB_VIEW_PARAM) === "documents"
  const view = parseSubView(PLAN_VIEWS, searchParams.get(SUB_VIEW_PARAM))
  const { loadThemes, loadCoverageMatrix, loadThemeSuggestionStatus, reset } = useStrategyStore()

  // Same load StrategySection does for its win-theme views: themes, coverage
  // and suggestion status, reset on the way out. Switching between Brief and
  // Win themes does not re-run it.
  useEffect(() => {
    if (wasDocuments) return
    void loadThemes(bidId)
    void loadCoverageMatrix(bidId)
    void loadThemeSuggestionStatus(bidId)
    return () => reset()
  }, [bidId, wasDocuments, loadThemes, loadCoverageMatrix, loadThemeSuggestionStatus, reset])

  if (wasDocuments) {
    const target = legacyBidRedirectTarget(bidId, "responses", location.search)
    return <Navigate replace to={{ ...target, hash: location.hash }} />
  }

  return (
    <div className="flex h-full flex-col">
      <BidSubNav label="Plan" views={PLAN_VIEWS} active={view} />
      <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
        {view === "win-themes" && <WinThemesList bidId={bidId} readOnly={readOnly} />}
        {/* Bid Brief with bid memory beside it (below it on narrower screens). */}
        {view === "brief" && (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start">
            <BidBrief bidId={bidId} readOnly={readOnly} />
            <BidMemoryList bidId={bidId} readOnly={readOnly} />
          </div>
        )}
      </div>
    </div>
  )
}
